import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Variáveis de ambiente do Supabase não encontradas. Por favor, verifique seu arquivo .env.'
  );
  throw new Error('Configuração do Supabase incompleta');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'inspirafolio-auth',
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Helper function to check if user is authenticated
export const isAuthenticated = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return !!data.session;
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return false;
  }
};

// Helper function to get current user
export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return data.user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Helper function to get user profile
export const getUserProfile = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

// Configuração dos buckets
export const STORAGE_BUCKETS = {
  POSTS: 'posts',
  AVATARS: 'avatars',
  TEMP: 'temp'
} as const;

// Função para inicializar todos os buckets necessários
export const initializeBuckets = async () => {
  try {
    for (const bucketName of Object.values(STORAGE_BUCKETS)) {
      await ensureBucketExists(bucketName);
    }
    console.log('Todos os buckets foram inicializados com sucesso');
    return true;
  } catch (error) {
    console.error('Erro ao inicializar buckets:', error);
    return false;
  }
};

// Função para garantir que o bucket existe
const ensureBucketExists = async (bucketName: string) => {
  try {
    // Verificar se o bucket já existe
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error('Erro ao listar buckets:', listError);
      // Se o erro for de permissão, podemos tentar criar o bucket mesmo assim
      if (!listError.message?.includes('Permission denied')) {
        throw listError;
      }
    }

    const bucketExists = buckets?.some(bucket => bucket.name === bucketName);

    if (!bucketExists) {
      console.log(`Criando bucket ${bucketName}...`);
      // Criar o bucket se não existir
      const { error: createError } = await supabase.storage.createBucket(bucketName, {
        public: true,
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
        fileSizeLimit: 5242880 // 5MB em bytes
      });

      if (createError) {
        console.error(`Erro ao criar bucket ${bucketName}:`, createError);
        // Se o erro for porque o bucket já existe, podemos ignorar
        if (!createError.message?.includes('already exists')) {
          throw createError;
        }
      }

      // Definir política de acesso público para o bucket
      await supabase.storage.from(bucketName).upload('policy.json', JSON.stringify({
        version: '1.0',
        public: true
      }), {
        contentType: 'application/json',
        upsert: true
      });

      console.log(`Bucket ${bucketName} criado e configurado com sucesso`);
    } else {
      console.log(`Bucket ${bucketName} já existe`);
    }

    return true;
  } catch (error) {
    console.error(`Erro ao verificar/criar bucket ${bucketName}:`, error);
    throw error;
  }
};

// Helper function to upload a file to storage
export const uploadFile = async (bucket: string, filePath: string, file: File) => {
  try {
    // Garantir que o bucket existe
    await ensureBucketExists(bucket);

    // Get current user
    const { data: userData } = await supabase.auth.getUser();
    if (!userData || !userData.user) {
      throw new Error('Usuário não autenticado');
    }
    
    console.log('Iniciando upload:', {
      bucket,
      filePath,
      fileSize: file.size,
      fileType: file.type,
      userId: userData.user.id
    });

    // Verificar se o arquivo é válido
    if (!file || file.size === 0) {
      throw new Error('Arquivo inválido');
    }

    // Verificar o tamanho do arquivo (5MB)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('O arquivo deve ter no máximo 5MB');
    }

    // Verificar o tipo do arquivo
    if (!file.type.startsWith('image/')) {
      throw new Error('O arquivo deve ser uma imagem');
    }

    // Usar o filePath fornecido ou gerar um novo
    let fullPath = filePath;
    if (!filePath) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      fullPath = `${userData.user.id}/${fileName}`;
    }

    console.log('Caminho completo do arquivo:', fullPath);

    // Verificar se o bucket está acessível
    const { data: bucketInfo, error: bucketError } = await supabase.storage.getBucket(bucket);
    if (bucketError) {
      console.error('Erro ao acessar bucket:', bucketError);
      throw new Error(`Erro ao acessar bucket: ${bucketError.message}`);
    }

    console.log('Bucket encontrado:', bucketInfo);

    // Upload the file
    let uploadData = null;
    const { data, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fullPath, file, {
        cacheControl: '3600',
        upsert: true,
        contentType: file.type
      });
    
    if (uploadError) {
      console.error('Erro detalhado no upload:', uploadError);
      // Se o erro for de bucket não encontrado, tentar criar novamente
      if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('new row violates row-level security policy')) {
        console.log('Tentando recriar o bucket e políticas...');
        await ensureBucketExists(bucket);

        // Tentar o upload novamente
        console.log('Tentando upload novamente...');
        const { data: retryData, error: retryError } = await supabase.storage
          .from(bucket)
          .upload(fullPath, file, {
            cacheControl: '3600',
            upsert: true,
            contentType: file.type
          });
        
        if (retryError) {
          console.error('Erro na segunda tentativa de upload:', retryError);
          throw retryError;
        }
        
        uploadData = retryData;
        console.log('Upload bem sucedido na segunda tentativa:', uploadData);
      } else {
        throw uploadError;
      }
    } else {
      uploadData = data;
      console.log('Upload bem sucedido na primeira tentativa:', uploadData);
    }
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(fullPath);
      
    console.log('URL pública gerada:', publicUrl);

    return {
      data: {
        publicUrl,
        path: fullPath
      },
      error: null
    };
  } catch (error: any) {
    console.error('Erro no processo de upload:', error);
    return {
      data: null,
      error: {
        message: error.message || 'Erro desconhecido no upload',
        details: error.details || '',
        hint: error.hint || '',
        code: error.code || ''
      }
    };
  }
};

// Helper function to handle Supabase errors
export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

// Helper function to create a post
export const createPost = async (postData: any) => {
  try {
    const { data, error } = await supabase
      .from('posts')
      .insert(postData)
      .select()
      .single();
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error creating post:', error);
    return { data: null, error };
  }
};



// Helper function to delete user profile and related data
export const deleteUserProfile = async (userId: string) => {
  try {
    // 0. Verificar se o usuário está autenticado e obter a sessão
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Usuário não autenticado');
    }

    if (session.user.id !== userId) {
      throw new Error('Usuário não autorizado');
    }

    // 1. Excluir notificações do usuário
    const { error: notificationsError } = await supabase
      .from('notifications')
      .delete()
      .or(`user_id.eq.${userId},actor_id.eq.${userId}`);

    if (notificationsError) {
      console.error('Erro ao excluir notificações:', notificationsError);
    }

    // 2. Excluir saved posts do usuário
    const { error: savedPostsError } = await supabase
      .from('saved_posts')
      .delete()
      .eq('user_id', userId);

    if (savedPostsError) {
      console.error('Erro ao excluir posts salvos:', savedPostsError);
    }

    // 3. Excluir post views do usuário
    const { error: postViewsError } = await supabase
      .from('post_views')
      .delete()
      .eq('user_id', userId);

    if (postViewsError) {
      console.error('Erro ao excluir visualizações:', postViewsError);
    }

    // 4. Excluir likes do usuário
    const { error: likesError } = await supabase
      .from('likes')
      .delete()
      .eq('user_id', userId);

    if (likesError) {
      console.error('Erro ao excluir curtidas:', likesError);
    }

    // 5. Excluir comentários do usuário
    const { error: commentsError } = await supabase
      .from('comments')
      .delete()
      .eq('user_id', userId);

    if (commentsError) {
      console.error('Erro ao excluir comentários:', commentsError);
    }

    // 6. Excluir follows do usuário
    const { error: followsError } = await supabase
      .from('follows')
      .delete()
      .or(`follower_id.eq.${userId},following_id.eq.${userId}`);

    if (followsError) {
      console.error('Erro ao excluir conexões:', followsError);
    }

    // 7. Excluir todas as imagens do storage antes de excluir os posts
    const buckets = ['posts', 'avatars', 'covers'];
    for (const bucket of buckets) {
      try {
        const { data: files } = await supabase.storage
          .from(bucket)
          .list(userId);

        if (files && files.length > 0) {
          const filePaths = files.map(file => `${userId}/${file.name}`);
          const { error: removeError } = await supabase.storage
            .from(bucket)
            .remove(filePaths);

          if (removeError) {
            console.error(`Erro ao excluir arquivos do bucket ${bucket}:`, removeError);
          }
        }
      } catch (error) {
        console.error(`Erro ao listar arquivos do bucket ${bucket}:`, error);
      }
    }

    // 8. Excluir posts do usuário
    const { error: postsError } = await supabase
      .from('posts')
      .delete()
      .eq('user_id', userId);

    if (postsError) {
      console.error('Erro ao excluir posts:', postsError);
    }

    // 9. Excluir o perfil do usuário
    const { error: profileError } = await supabase
      .from('profiles')
      .delete()
      .eq('id', userId);

    if (profileError) {
      console.error('Erro ao excluir perfil:', profileError);
      throw new Error('Erro ao excluir perfil do usuário');
    }

    // 10. Excluir o usuário do auth
    const { error: deleteError } = await supabase.auth.admin.deleteUser(userId);

    if (deleteError) {
      console.error('Erro ao excluir usuário:', deleteError);
      throw new Error('Erro ao excluir usuário do sistema');
    }

    // 11. Fazer logout do usuário
    const { error: signOutError } = await supabase.auth.signOut();

    if (signOutError) {
      console.error('Erro ao fazer logout:', signOutError);
    }

    return { success: true, error: null };
  } catch (error: any) {
    console.error('Erro ao excluir perfil:', error);
    
    // Retorna uma mensagem de erro mais amigável
    const errorMessage = error.message === 'Usuário não autenticado'
      ? 'Você precisa estar autenticado para excluir seu perfil.'
      : error.message === 'Usuário não autorizado'
      ? 'Você não tem permissão para excluir este perfil.'
      : error.message === 'Erro ao excluir perfil do usuário'
      ? 'Não foi possível excluir o perfil. Por favor, tente novamente mais tarde.'
      : 'Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.';
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

// Função para enviar email de recuperação de senha
export const sendPasswordResetEmail = async (email: string) => {
  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`
    });

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Erro ao enviar email de recuperação:', error);
    return { success: false, error };
  }
};

// Função para atualizar a senha
export const updatePassword = async (newPassword: string) => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Erro ao atualizar senha:', error);
    return { success: false, error };
  }
};